import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import config from '@config';
import { renderTitleWithStyledQuestionMarks } from './renderTitle';

import './Onboarding.css';

/**
 * Variante "comodines": presenta los cambios (comodines) para soci@s.
 * En el último paso ofrece el CTA de suscripción al Plan Anual.
 */
export const ComodinesOnboarding = ({ slides, userEmail, onClose, onGoToRules }) => {
  const [onboardingStep, setOnboardingStep] = useState(0);

  const onNextTutorialStep = () => {
    setOnboardingStep((prev) => Math.min(prev + 1, slides.length - 1));
  };

  const goToRulesFromTutorial = () => {
    onClose();
    onGoToRules();
  };

  const buildSociosUrl = useCallback(() => {
    if (!config.sociosURL) return null;
    const returnUrl =
      typeof window !== 'undefined' ? window.location.pathname : '';

    try {
      const url = new URL(config.sociosURL);
      if (config.clientId) url.searchParams.set('source', config.clientId);
      if (userEmail) url.searchParams.set('partner_email', userEmail);
      if (returnUrl) url.searchParams.set('return_url', returnUrl);
      return url.toString();
    } catch (error) {
      const separator = config.sociosURL.includes('?') ? '&' : '?';
      const sourceParam = config.clientId
        ? `source=${encodeURIComponent(config.clientId)}`
        : '';
      const emailParam = userEmail
        ? `partner_email=${encodeURIComponent(userEmail)}`
        : '';
      const returnUrlParam = returnUrl
        ? `return_url=${encodeURIComponent(returnUrl)}`
        : '';
      const query = [sourceParam, emailParam, returnUrlParam]
        .filter(Boolean)
        .join('&');
      return query ? `${config.sociosURL}${separator}${query}` : config.sociosURL;
    }
  }, [userEmail]);

  const onSubscribe = useCallback(() => {
    const sociosLink = buildSociosUrl();
    if (!sociosLink) return;
    window.location.assign(sociosLink);
  }, [buildSociosUrl]);

  const isLastStep = onboardingStep === slides.length - 1;
  const isFirstStep = onboardingStep === 0;
  const currentSlide = slides[onboardingStep];

  if (!currentSlide) return null;

  return (
    <div className="home-onboarding-overlay" role="dialog" aria-modal="true">
      <div className="home-onboarding-popup home-onboarding-popup--comodines">
        <div
          className={`home-onboarding-content`}
        >
          <div className="home-onboarding-text">
            <h2>{renderTitleWithStyledQuestionMarks(currentSlide.title, "comodines")}</h2>
            {currentSlide.paragraphs.map((paragraph, idx) => (
              <p key={`${currentSlide.id}-paragraph-${idx}`} className={isFirstStep ? "mb-text-partner" : ""}>{paragraph}</p>
            ))}
            {isFirstStep ? <div className='home-onboarding-text-partner'>
              <span className='red'>Asociate ahora, activa el cambio y seguí sumando puntos</span>
              <button
                type="button"
                className="home-onboarding-primary"
                onClick={onSubscribe}
              >
                Suscribirme a SOCI@S
              </button>
            </div> : null}
          </div>
          <div className="home-onboarding-image">
            {currentSlide.imageSrc ? (
              <img src={currentSlide.imageSrc} alt={currentSlide.imageAlt} />
            ) : (
              <div className="home-onboarding-image-placeholder">
                Imagen paso {onboardingStep + 1}
              </div>
            )}
          </div>
        </div>
        <div className="home-onboarding-actions">
          {isFirstStep ? (
            <>
              <button
                type="button"
                className="home-onboarding-default"
                onClick={onClose}
              >
                Omitir tutorial
              </button>
              <button
                type="button"
                className="home-onboarding-skip"
                onClick={onNextTutorialStep}
              >
                Siguiente
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="home-onboarding-primary"
                onClick={onSubscribe}
              >
                Suscribirme a SOCI@S
              </button>
              <button
                type="button"
                className="home-onboarding-secondary"
                onClick={goToRulesFromTutorial}
              >
                Consultar reglas
              </button>
              <button
                type="button"
                className="home-onboarding-skip"
                onClick={onClose}
              >
                ¡Empezar a jugar!
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

ComodinesOnboarding.displayName = 'ComodinesOnboarding';
ComodinesOnboarding.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.object).isRequired,
  userEmail: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onGoToRules: PropTypes.func.isRequired,
};
ComodinesOnboarding.defaultProps = {
  userEmail: '',
};

export default ComodinesOnboarding;
